import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Button,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { LEVEL_OPTIONS, LANGUAGE_OPTIONS, TAG_OPTIONS} from "components/constant";

const initialFields = {
  questionBankName: { value: "", error: "" },
  language: { value: "", error: "" },
  level: { value: "", error: "" },
  tag: { value: "", error: "" },
};

const LanguageModal = ({ isOpen, toggleModal, onSave }) => {
  const [fields, setFields] = useState(initialFields);
  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFields(initialFields);
    }
  }, [isOpen]);

  const handleChange = (name, value) => {
    setFields((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "questionBankName":
        return !trimmed ? "Please enter question bank name" : "";

      case "language":
        return !trimmed ? "Please select language" : "";

      case "level":
        return !trimmed ? "Please select level" : "";

      case "tag":
        return !trimmed ? "Please select tag" : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let updated = { ...fields };

    Object.keys(updated).forEach((key) => {
      updated[key] = {
        ...updated[key],
        error: getValidationError(key, updated[key].value),
      };
    });

    setFields(updated);

    return Object.values(updated).every((item) => item.error === "");
  };

  const handleSave = () => {
    if (!validateForm()) return;

    onSave(fields.language.value);
  };

  return (
    <>
      {isOpen && <div className="blur-overlay"></div>}

      <Modal
        isOpen={isOpen}
        toggle={toggleModal}
        backdrop={false}
        keyboard={false}
        className="modal-dialog-centered"
      >
        <ModalHeader toggle={toggleModal}>
          Please select question details
        </ModalHeader>

        <ModalBody>
          {/* QUESTION BANK NAME */}
          <div className="mb-3">
            <Label>
              Question Bank Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter question bank name"
              value={fields.questionBankName.value}
              onChange={(e) => handleChange("questionBankName", e.target.value)}
            />
            {fields.questionBankName.error && (
              <div className="text-danger">{fields.questionBankName.error}</div>
            )}
          </div>

          {/* LANGUAGE */}
          <div className="mb-4">
            <Label>
              Language <span className="text-danger">*</span>
            </Label>
            <div className="language-options">
              {LANGUAGE_OPTIONS.map((item) => (
                <div
                  key={item.value}
                  className={`language-card ${
                    fields.language.value === item.label ? "active" : ""
                  }`}
                  onClick={() => handleChange("language", item.label)}
                >
                  <Input
                    type="radio"
                    name="language"
                    checked={fields.language.value === item.label}
                    onChange={() => handleChange("language", item.label)}
                  />
                  <span className="ml-2">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <Row className="mb-3">
            <Col md="6">
              <Label>
                Level <span className="text-danger">*</span>
              </Label>
              <Select
                styles={fixedWidthCustomStyle("100%")}
                options={LEVEL_OPTIONS}
                placeholder="Select Level"
                value={LEVEL_OPTIONS.find(
                  (item) => item.value === fields.level.value,
                )}
                onChange={(selected) => handleChange("level", selected.value)}
              />
              {fields.level.error && (
                <div className="text-danger">{fields.level.error}</div>
              )}
            </Col>

            <Col md="6">
              <Label>
                Tag <span className="text-danger">*</span>
              </Label>
              <Select
                styles={fixedWidthCustomStyle("100%")}
                options={TAG_OPTIONS}
                placeholder="Select Tag"
                value={TAG_OPTIONS.find(
                  (item) => item.value === fields.tag.value,
                )}
                onChange={(selected) => handleChange("tag", selected.value)}
              />
              {fields.tag.error && (
                <div className="text-danger">{fields.tag.error}</div>
              )}
            </Col>
          </Row>

          {/* ACTION BUTTONS */}
          <div className="d-flex justify-content-end mt-4">
            <Button
              color="secondary"
              onClick={toggleModal}
              className="mr-2 px-4"
            >
              Cancel
            </Button>

            <Button
              color="deep-blue"
              onClick={handleSave}
              disabled={
                !fields.questionBankName.value ||
                !fields.language.value ||
                !fields.level.value ||
                !fields.tag.value
              }
              className="px-4"
            >
              Save
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default LanguageModal;
